// Domänen-Konstanten: Kategorien, Lagerorte, Quellen, Mahlzeiten.
// Bisher waren diese Listen über Frontend und Backend verstreut dupliziert.

// ── Kategorien ────────────────────────────────────────────────────────
// Die Reihenfolge ist der Standard-Laufweg durch einen deutschen Supermarkt
// und dient als Vorbelegung für die Sortierung der Einkaufsliste
// (pro Markt individuell überschreibbar, siehe `store_layout`).
export interface CategoryDef {
  key: string;
  label: string;
  icon: string;
}

export const CATEGORIES: CategoryDef[] = [
  { key: 'obst_gemuese', label: 'Obst & Gemüse', icon: '🥬' },
  { key: 'backwaren', label: 'Backwaren', icon: '🥖' },
  { key: 'milchprodukte', label: 'Milchprodukte', icon: '🥛' },
  { key: 'kaese', label: 'Käse', icon: '🧀' },
  { key: 'fleisch_wurst', label: 'Fleisch & Wurst', icon: '🥩' },
  { key: 'fisch', label: 'Fisch', icon: '🐟' },
  { key: 'tiefkuehl', label: 'Tiefkühl', icon: '🧊' },
  { key: 'teigwaren_reis', label: 'Teigwaren & Reis', icon: '🍝' },
  { key: 'konserven', label: 'Konserven & Vorräte', icon: '🥫' },
  { key: 'gewuerze_backen', label: 'Gewürze & Backzutaten', icon: '🧂' },
  { key: 'snacks_suesses', label: 'Snacks & Süßes', icon: '🍫' },
  { key: 'getraenke', label: 'Getränke', icon: '🧃' },
  { key: 'haushalt', label: 'Haushalt', icon: '🧻' },
  { key: 'drogerie', label: 'Drogerie', icon: '🧴' },
  { key: 'sonstiges', label: 'Sonstiges', icon: '📦' },
];

export const FALLBACK_CATEGORY = 'sonstiges';

const BY_LABEL = new Map(CATEGORIES.map((c) => [c.label.toLowerCase(), c]));
const BY_KEY = new Map(CATEGORIES.map((c) => [c.key, c]));

/** Kategorie-Definition zu Key ODER (historischem) Label finden. */
export function findCategory(value: string | null | undefined): CategoryDef {
  if (!value) return BY_KEY.get(FALLBACK_CATEGORY)!;
  return BY_KEY.get(value) ?? BY_LABEL.get(value.toLowerCase()) ?? BY_KEY.get(FALLBACK_CATEGORY)!;
}

export function categoryLabel(value: string | null | undefined): string {
  return findCategory(value).label;
}

/** Index im Standard-Laufweg (für Sortierung, unbekannt = ans Ende). */
export function categoryOrder(value: string | null | undefined): number {
  const key = findCategory(value).key;
  const idx = CATEGORIES.findIndex((c) => c.key === key);
  return idx === -1 ? CATEGORIES.length : idx;
}

// ── Lagerorte ─────────────────────────────────────────────────────────
export const STORAGE_LOCATIONS = [
  { key: 'kuehlschrank', label: 'Kühlschrank', icon: '🧊' },
  { key: 'gefrierschrank', label: 'Gefrierschrank', icon: '❄️' },
  { key: 'vorratsschrank', label: 'Vorratsschrank', icon: '🚪' },
  { key: 'keller', label: 'Keller', icon: '🪜' },
  { key: 'sonstiges', label: 'Sonstiges', icon: '📦' },
] as const;

export type StorageLocation = (typeof STORAGE_LOCATIONS)[number]['key'];

// ── Quellen ───────────────────────────────────────────────────────────
/** Woher kommt ein Einkaufslisten-Eintrag? */
export const SHOPPING_SOURCES = [
  'manual',
  'siri',
  'auto_minstock',
  'auto_weekplan',
  'auto_replenish',
  'auto_expiry',
  'photo_diff',
  'receipt',
] as const;
export type ShoppingSource = (typeof SHOPPING_SOURCES)[number];

/** Label für Herkunfts-Badges (null = kein Badge anzeigen). */
export const SHOPPING_SOURCE_LABEL: Record<ShoppingSource, string | null> = {
  manual: null,
  siri: 'Siri',
  auto_minstock: 'Meldebestand',
  auto_weekplan: 'Wochenplan',
  auto_replenish: 'Nachkauf-Rhythmus',
  auto_expiry: 'Läuft ab',
  photo_diff: 'Foto',
  receipt: 'Bon',
};

/** Woher kommt eine Bestandsbuchung? */
export const INVENTORY_SOURCES = [
  'receipt',
  'photo_snapshot',
  'recipe_cooked',
  'siri_manual',
  'barcode_scan',
  'correction',
  'wasted',
  'consumed',
] as const;
export type InventorySource = (typeof INVENTORY_SOURCES)[number];

/** Quellen, die den Bestand „erden" (last_confirmed_at aktualisieren). */
export const GROUNDING_SOURCES = new Set<string>([
  'receipt',
  'photo_snapshot',
  'barcode_scan',
  'wasted',
]);

// ── Mahlzeiten ────────────────────────────────────────────────────────
export const MEAL_TYPES = [
  { key: 'fruehstueck', label: 'Frühstück', icon: '🌅' },
  { key: 'mittagessen', label: 'Mittagessen', icon: '☀️' },
  { key: 'abendessen', label: 'Abendessen', icon: '🌙' },
  { key: 'snack', label: 'Snack', icon: '🍎' },
] as const;

export type MealType = (typeof MEAL_TYPES)[number]['key'];

export function mealLabel(key: string | null | undefined): string {
  return MEAL_TYPES.find((m) => m.key === key)?.label ?? 'Mahlzeit';
}

/** Sinnvolle Vorbelegung des Mahlzeitentyps nach Uhrzeit. */
export function defaultMealType(hour: number = new Date().getHours()): MealType {
  if (hour < 10) return 'fruehstueck';
  if (hour < 15) return 'mittagessen';
  if (hour < 21) return 'abendessen';
  return 'snack';
}

// ── Frische / Haltbarkeit ─────────────────────────────────────────────
export type Freshness = 'high' | 'medium' | 'low';

export const FRESHNESS_LABEL: Record<Freshness, string> = {
  high: 'frisch bestätigt',
  medium: 'vor einer Weile',
  low: 'unsicher',
};

/** Ab wie vielen Tagen Restlaufzeit warnen wir? */
export const EXPIRY_WARN_DAYS = 3;
export const EXPIRY_SOON_DAYS = 7;

export type ExpiryLevel = 'expired' | 'critical' | 'soon' | 'ok';

export function expiryLevel(daysLeft: number | null | undefined): ExpiryLevel {
  if (daysLeft === null || daysLeft === undefined) return 'ok';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= EXPIRY_WARN_DAYS) return 'critical';
  if (daysLeft <= EXPIRY_SOON_DAYS) return 'soon';
  return 'ok';
}

export const EXPIRY_LABEL: Record<ExpiryLevel, string> = {
  expired: 'abgelaufen',
  critical: 'läuft bald ab',
  soon: 'bald fällig',
  ok: 'haltbar',
};

/**
 * Typische Haltbarkeit ab Kaufdatum in Tagen, je Kategorie – nur als
 * Vorbelegung, wenn für ein Produkt noch keine eigene Historie existiert.
 */
export const DEFAULT_SHELF_LIFE_DAYS: Record<string, number> = {
  obst_gemuese: 7,
  backwaren: 3,
  milchprodukte: 10,
  kaese: 21,
  fleisch_wurst: 4,
  fisch: 2,
  tiefkuehl: 180,
  teigwaren_reis: 540,
  konserven: 730,
  gewuerze_backen: 540,
  snacks_suesses: 180,
  getraenke: 365,
  haushalt: 0,
  drogerie: 0,
  sonstiges: 30,
};
